import React, { ReactNode, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Body, Label, SelectionTileGroup } from '@sato/components';
import { observer } from 'mobx-react-lite';
import * as yup from 'yup';

import { IWidget, ParsedEntry } from '@/contentful/types';

import { api } from '@api';
import { HookFormCheckbox } from '@components/ReactHookFormFields/HookFormCheckbox';
import { HookFormNumberInput } from '@components/ReactHookFormFields/HookFormNumberInput';
import { HookFormSelectionTileGroup } from '@components/ReactHookFormFields/HookFormSelectionTileGroup';
import { useIncidentCreation } from '@hooks/incidents';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import {
  IncidentClassification,
  IncidentSubClassification,
  KeyType,
} from '@models';
import { apartmentStore } from '@stores/apartmentStore';

import { IncidentFormWrapper } from '../IncidentFormWrapper';

interface RequestNewKeyForm {
  quantity: number;
  keyType: KeyType;
  acknowledgeFees: boolean;
}

export const KeysRequestForm = observer(
  ({
    children,
    customTranslations,
  }: {
    children?: ReactNode;
  } & ParsedEntry<IWidget>) => {
    const { t, tContentful } = useCustomTranslations(customTranslations);

    const { create } = useIncidentCreation({
      postIncident: api.incidents.postNewKeyRequest,
      classifications: {
        classification: IncidentClassification.KEYS,
        subClassification: IncidentSubClassification.ORDER_NEW_KEY,
      },
    });

    return (
      <IncidentFormWrapper<RequestNewKeyForm>
        incidentKind="KeysRequest"
        defaultValues={{
          quantity: undefined,
          keyType: undefined,
          acknowledgeFees: false,
        }}
        validationSchema={yup.object().shape({
          quantity: yup.number().required().min(1),
          keyType: yup.string().oneOf(Object.values(KeyType)).required(),
          acknowledgeFees: yup
            .boolean()
            .isTrue(tContentful('error.required_field'))
            .required(),
        })}
        onSubmit={create}
      >
        {apartmentStore.selectedApartment?.realestate.lockType && (
          <Body className="px-1">
            <Label>{tContentful('lockType')}</Label>
            {tContentful(
              `lockType.${apartmentStore.selectedApartment.realestate.lockType}`
            )}
          </Body>
        )}

        <KeyAmountSelector
          inputTitle={t('otherAmountInput')}
          label={t('amountText')}
          otherAmountSelectionTitle={t('otherAmount')}
        />
        <HookFormSelectionTileGroup
          name="keyType"
          label={t('keyType')}
          tileVariant="horizontal"
          groupClasses="flex-col"
          orientation="vertical"
          staticFieldMessageSpace={false}
          items={[
            {
              value: KeyType.APARTMENT,
              title: t('apartmentKey'),
            },
            {
              value: KeyType.COMMON,
              title: t('commonKey'),
            },
          ]}
        />

        <HookFormCheckbox
          name="acknowledgeFees"
          label={tContentful('keysForm.acknowledgeFees')}
          className="mt-3"
        />
        {children}
      </IncidentFormWrapper>
    );
  }
);

const KeyAmountSelector = ({
  inputTitle,
  label,
  otherAmountSelectionTitle,
}: {
  label: string;
  otherAmountSelectionTitle: string;
  inputTitle: string;
}) => {
  const [isOtherAmountVisible, setIsOtherAmountVisible] = useState(false);
  const { getFieldState, setValue } = useFormContext<RequestNewKeyForm>();

  const state = getFieldState('quantity');

  return (
    <div>
      <SelectionTileGroup<'1' | '2' | 'other'>
        items={[
          { title: '1', value: '1' },
          { title: '2', value: '2' },
          {
            title: otherAmountSelectionTitle,
            value: 'other',
          },
        ]}
        label={label}
        className="mb-0"
        onChange={(value) => {
          setIsOtherAmountVisible(value === 'other');
          if (value !== 'other') setValue('quantity', Number(value));
        }}
        error={
          !isOtherAmountVisible && state.isTouched
            ? state.error?.message
            : undefined
        }
        staticFieldMessageSpace={false}
      />

      {isOtherAmountVisible && (
        <HookFormNumberInput
          name="quantity"
          min={1}
          label={inputTitle}
          wrapperClassName="my-3"
        />
      )}
    </div>
  );
};
